export type CanonicalRole =
  | 'developer'
  | 'system_admin'
  | 'finance_manager'
  | 'general_manager'
  | 'sales_manager'
  | 'salesperson'
  | 'other';

const ROLE_LABELS: Record<CanonicalRole, string> = {
  developer: 'Developer',
  system_admin: 'System Admin',
  finance_manager: 'Finance Manager',
  general_manager: 'General Manager',
  sales_manager: 'Sales Manager',
  salesperson: 'Salesperson',
  other: 'Other',
};

const LEGACY_ROLE_ALIASES: Record<string, CanonicalRole> = {
  admin: 'system_admin',
  manager: 'general_manager',
  user: 'other',
};

export interface ParsedRole {
  baseRole: CanonicalRole;
  customLabel?: string;
  permissions: string[];
}

const CANONICAL_ROLES: ReadonlySet<CanonicalRole> = new Set<CanonicalRole>([
  'developer',
  'system_admin',
  'finance_manager',
  'general_manager',
  'sales_manager',
  'salesperson',
  'other',
]);

export const normalizeRole = (role?: string | null): CanonicalRole => {
  if (!role) return 'other';
  const raw = role.trim().toLowerCase();
  const normalizedRaw = raw.replace(/[\s-]+/g, '_');
  const base = normalizedRaw.split('|', 1)[0];
  if (base === 'other') return 'other';

  const legacyMapped = LEGACY_ROLE_ALIASES[base];
  if (legacyMapped) return legacyMapped;

  if (CANONICAL_ROLES.has(base as CanonicalRole)) {
    return base as CanonicalRole;
  }

  return 'other';
};

export const parseRole = (role?: string | null): ParsedRole => {
  const normalized = normalizeRole(role);
  if (!role) {
    return { baseRole: normalized, permissions: [] };
  }

  const [baseRaw, labelRaw = '', permissionsRaw = ''] = role.split('|');
  const baseRole = normalizeRole(baseRaw);
  const customLabel = baseRole === 'other' && labelRaw ? decodeURIComponent(labelRaw) : undefined;
  const permissions = permissionsRaw
    ? permissionsRaw
        .split(',')
        .map((permission) => permission.trim())
        .filter(Boolean)
    : [];

  return {
    baseRole,
    customLabel,
    permissions,
  };
};

export const buildRoleValue = (
  baseRole: CanonicalRole,
  customLabel?: string,
  permissions: string[] = []
): string => {
  if (baseRole !== 'other') {
    return baseRole;
  }

  const safeLabel = encodeURIComponent((customLabel || 'Other').trim());
  const uniquePermissions = Array.from(new Set(permissions));
  const serializedPermissions = uniquePermissions.join(',');
  return `other|${safeLabel}|${serializedPermissions}`;
};

export const getRoleLabel = (role?: string | null): string => {
  const parsed = parseRole(role);
  if (parsed.baseRole === 'other' && parsed.customLabel) {
    return parsed.customLabel;
  }
  return ROLE_LABELS[parsed.baseRole] || 'Other';
};

export const canManageUsers = (role?: string | null): boolean => {
  const normalized = normalizeRole(role);
  return normalized === 'system_admin';
};

export const canViewUsers = (role?: string | null): boolean => {
  const normalized = normalizeRole(role);
  return ['system_admin', 'finance_manager', 'general_manager', 'sales_manager'].includes(
    normalized
  );
};

export const canViewCosts = (role?: string | null): boolean => {
  const normalized = normalizeRole(role);
  return ['system_admin', 'general_manager', 'finance_manager', 'sales_manager'].includes(
    normalized
  );
};

export const isSalesAssignableRole = (role?: string | null): boolean => {
  const normalized = normalizeRole(role);
  return ['system_admin', 'general_manager', 'sales_manager', 'salesperson'].includes(normalized);
};
